import { Component, OnDestroy, Input, OnInit} from '@angular/core';
import { Router } from '@angular/router';
import { LoadingService, PATHS } from '@app/core/services';
import { navItems } from '@app/_nav';
import { AuthConfigurationService } from '@core/auth/auth-configuration.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { filter, map, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AnnouncementService } from '../../core/services/announcement.service';
import { AnnouncementComponent } from '../../modules/announcements/announcement.component';
import { Notification, NotificationType, getNotificationInput } from '@app/core/api/models/notifications.model';
import { NotificationsService } from '@app/core/api/services/notifications.service';

@Component({
    selector: "app-dashboard",
    templateUrl: './authenticated-layout.component.html',
    styleUrls: ['./authenticated-layout.component.scss'],
    standalone: false
})
export class AuthenticatedLayoutComponent implements OnInit, OnDestroy {
  public sidebarVisible = true;
  public navItems = navItems;
  isAuthenticated$: Observable<boolean>;
  username$: Observable<string>;
  userInitial$: Observable<string>;
  userName$: Observable<string>;
  currentYear: number;
  unreadCount = 0;
  private announcementDialogRef: MatDialogRef<AnnouncementComponent> | null = null;
  userId: string = ''; 
  subscriptions = new Subscription();
  destroy$: Subject<void> = new Subject();
  @Input() notices$!: Observable<Notification[]>;

  constructor(
    public oidcSecurityService: OidcSecurityService,
    private authService: AuthConfigurationService,
    private router: Router,
    private loadingService: LoadingService,
    private announcementService: AnnouncementService,
    private dialog: MatDialog,
    private NotificationsService: NotificationsService
  ) { }

  ngOnInit() {
    this.isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
      map(result => result.isAuthenticated)
    );

    this.userInitial$ = this.oidcSecurityService.userData$.pipe(
      map((claimsOrResult: any) => claimsOrResult?.userData ?? claimsOrResult),
      map((claims: any) => {
        const name = claims?.name || claims?.email || claims?.preferred_username || '';
        return name.charAt(0).toUpperCase() || '?';
      })
    );

    this.userName$ = this.oidcSecurityService.userData$.pipe(
      map((claimsOrResult: any) => claimsOrResult?.userData ?? claimsOrResult),
      map((claims: any) => claims?.name || claims?.email || claims?.preferred_username || '')
    );

    // For footer year display
    this.currentYear = new Date().getFullYear();

    // Load Notifications (Announcements + Notices)
    const input: getNotificationInput = {
      AnnKey: 'SYS#1',
      Active: true
    };
    const notifications$ = this.NotificationsService.getNotifications(input);

    // Filter out notices
    this.notices$ = notifications$.pipe(
      map(results => results.filter(n => n.Type === NotificationType.Banner))
    );

    // Get the userId and then load the announcements for that user
    this.subscriptions.add(
      this.oidcSecurityService.userData$.pipe(
        filter(Boolean),
        map((claimsOrResult: any) => claimsOrResult?.userData ?? claimsOrResult),
        map((claims: any) =>
          claims?.sub ||
          claims?.['cognito:username'] ||
          claims?.preferred_username ||
          claims?.email ||
          ''
        ),
        filter((id: string) => !!id),
        distinctUntilChanged(),
        switchMap((id: string) => {
          this.userId = id;
          return notifications$;
        }),
        map(notifs => notifs.filter(n => n.Type === NotificationType.Announcement))   // filter announcements only
      ).subscribe((announcements => {
        // Set announcements in the service
        this.announcementService.setAnnouncements(announcements);
        this.unreadCount = this.announcementService.calculateUnreadCount(this.userId);
      })));
  }

  toggleSidebar() {
    this.sidebarVisible = !this.sidebarVisible;
  }
  openAnnouncements() {
    if (!this.announcementDialogRef) {
      this.announcementDialogRef = this.dialog.open(AnnouncementComponent, { 
        width: '600px', 
        panelClass: 'announcement-dialog-panel',
        data: { userId: this.userId } 
      });

      this.announcementDialogRef.afterClosed().subscribe(() => {
        this.unreadCount = this.announcementService.calculateUnreadCount(this.userId);
        this.announcementDialogRef = null;
      });

    }
  }

  login() {
    const path = PATHS.LOGIN.redirectPath();
    this.router.navigate([path]);
  }

  logout() {
    this.oidcSecurityService.logoff().subscribe();
    this.loadingService.generateObservableWithLoadingFeature(this.authService.LogOutOfCognito()).subscribe();
  }

  redirectToProfile() {
    const path = PATHS.PROFILE.redirectPath();
    this.router.navigate([path]);
  }

  redirectToAuthDebug() {
    const path = PATHS.AUTH.children.INFO.redirectPath();
    this.router.navigate([path]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }
}
